import React, { memo, useEffect, useState } from 'react';
import { DataTitle, Poppins, Spacer } from 'src/common';
import styled from 'styled-components';
import { ControllStatus, Modifiers, ProjectedScenario, Strategy } from 'src/api/types';
import TreatModifier from './Modifiers/TreatModifier';
import { FormikErrors, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import TransferModifier from './Modifiers/TransferModifier';
import _ from 'lodash';
import { Panel, Impact, Note } from './comps';
import Button from 'src/components/form/Button';
import { Info } from 'src/assets/dynamicSvg';
import { checkIsModifierStrategy } from './util';
import { useCache, KeyType } from 'src/graphql';
import { ModifierInput, useUpsertModifiersMutation } from 'src/graphql/_generated';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
import { ColorSet } from 'src/redux/reducers/theme';

const Div = styled.div<{ $bgColor?: keyof ColorSet }>`
  border-radius: 6px;
  background: ${({ theme, $bgColor }) => theme.colors[$bgColor || 'cultured']};
  padding: 16px 26px 30px 26px;
  position: relative;

  .content {
    display: flex;
    gap: 10px;

    &__main {
      flex: 1;
    }
  }

  .error-text {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 40px;

  .abandoned-note {
    margin-bottom: 18px;
    display: inline-block;
    color: ${({ theme }) => theme.colors.prussianBlue};
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 10px;

    svg {
      fill: ${({ theme }) => theme.colors.cflowerBlue};
    }
  }

  .btns {
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-end;
    gap: 10px;
  }
`;

const validationSchema = Yup.object({
  treat: Yup.array().of(
    Yup.object().shape({
      type: Yup.string().required('Required'),
      data: Yup.object().when(['type'], (values, schema) => {
        if (values[0] === 'TREAT_REDUCE_CONSEQUENCE') {
          return Yup.object().shape({
            lower: Yup.number().required('Required'),
            upper: Yup.number().required('Required'),
          });
        }
        if (values[0] === 'TREAT_REDUCE_FREQUENCY') {
          return Yup.object().shape({
            value: Yup.number().required('Required').min(0, 'Must be greater than 0'),
          });
        }

        return schema;
      }),
    }),
  ),
  transfer: Yup.array().of(
    Yup.object().shape({
      type: Yup.string().required('Required'),
      data: Yup.object().when(['type'], (values, schema) => {
        if (values[0] === 'TRANSFER_LIMIT') {
          return Yup.object().shape({
            excess: Yup.number().required('Required').min(0, 'Must be greater than 0'),
            tpc: Yup.number()
              .required('Required')
              .min(Yup.ref('excess'), 'Total Policy Cover must be greater than or equal to Excess'),
          });
        }

        return schema;
      }),
    }),
  ),
  notes: Yup.array().of(
    Yup.object().shape({
      type: Yup.string().required().equals(['NOTE_TYPE']),
      data: Yup.object().shape({
        note: Yup.string().nullable(),
      }),
    }),
  ),
});

const setDefaultIsPercentage = (treat: any) => {
  if (treat.type === 'TREAT_REDUCE_CONSEQUENCE' || treat.type === 'TREAT_REDUCE_FREQUENCY') {
    return {
      ...treat,
      data: {
        ...treat.data,
        isPercentage: treat.data?.isPercentage ?? false,
      },
    };
  }
  return treat;
};

interface ModifierProps {
  projectedScenario: ProjectedScenario;
  control: { id: any; strategy?: string | null; status?: string | null; modifiers?: Modifiers };
  useCallback?: (state: { values: Modifiers; errors: FormikErrors<Modifiers>; isValid: boolean }) => void;
  disabled?: boolean;
  isDirtyOutside?: boolean;
  onSave?: () => Promise<void>;
  addSubmitHandler?: (handler: () => void) => void;
  noResult?: boolean;
  withCopy?: boolean;
  startingValues: {
    frequency_times: number;
    lower: number;
    upper: number;
  };
  recommendedModifiers?: Modifiers | null;
  bgColor?: keyof ColorSet;
  cacheInvalidation?: KeyType[];
}

export const Modifier: React.FC<ModifierProps> = memo(
  ({
    projectedScenario,
    control,
    useCallback,
    disabled,
    isDirtyOutside,
    onSave,
    addSubmitHandler,
    noResult,
    withCopy,
    startingValues,
    recommendedModifiers,
    bgColor,
    cacheInvalidation,
  }) => {
    const [err, setErr] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const { refetchQuery } = useCache();

    const [update] = useUpsertModifiersMutation({
      onError: (err: Error) => {
        setErr(err.message);
        setIsSaving(false);
      },
    });

    const formik = useFormik({
      validationSchema,
      initialValues:
        control.modifiers ||
        ({
          treat: [],
          transfer: [],
          notes: [
            {
              type: 'NOTE_TYPE',
              data: {
                note: null,
              },
              isLive: false,
            },
          ],
        } as Modifiers),
      enableReinitialize: true,
      validateOnMount: true,
      onSubmit: async (values) => {
        const updatedValues = _.cloneDeep(values);
        updatedValues.treat = updatedValues.treat.map(setDefaultIsPercentage);

        setErr('');
        setIsSaving(true);
        const modifierInput: ModifierInput = {
          treat: updatedValues.treat.map((item) => ({
            type: item.type,
            data: item.data,
            note: item.note,
          })),
          transfer: updatedValues.transfer.map((item) => ({
            type: item.type,
            data: {
              ...(item.type === 'TRANSFER_LIMIT' && {
                excess: item.data.excess,
                tpc: item.data.tpc,
              }),
            },
            note: item.note,
          })),
          notes: updatedValues.notes.map((item) => ({
            type: item.type,
            data: {
              note: item.data.note,
            },
          })),
        };

        await update({
          variables: {
            control_id: control.id,
            scenario_id: projectedScenario.id,
            modifiers: modifierInput,
          },
        });

        if (cacheInvalidation) {
          await refetchQuery({ include: cacheInvalidation });
        }

        if (onSave) {
          await onSave();
        }

        setIsSaving(false);
        if (!addSubmitHandler) {
          showSuccessToast(toastMessages.modifier.saved);
        }
        return;
      },
    });

    const { values, errors, isValid, touched, isSubmitting, dirty, handleSubmit } = formik;

    useEffect(() => {
      if (useCallback && !isSubmitting) {
        useCallback({ values, errors, isValid });
      }
    }, [useCallback, values, errors, isValid, touched, isSubmitting]);

    useEffect(() => {
      if (addSubmitHandler) {
        addSubmitHandler(handleSubmit);
      }
    }, [addSubmitHandler]);

    return (
      <Div $bgColor={bgColor} data-cy="modifier">
        <DataTitle weight={600} css="user-select: none;">
          MODIFIERS
        </DataTitle>

        <FormikProvider value={formik}>
          {checkIsModifierStrategy(control.strategy) && (
            <Panel
              strategy={control.strategy as Strategy}
              withCopy={withCopy}
              recommendedModifiers={recommendedModifiers}
            />
          )}
          <Spacer $px={16} />
          <div className="content">
            <div className="content__main">
              {control.strategy === 'Treat' && (
                <TreatModifier disabled={disabled} noResult={noResult} recommendedModifiers={recommendedModifiers} />
              )}
              {control.strategy === 'Transfer' && (
                <TransferModifier
                  projectedScenario={projectedScenario}
                  disabled={disabled}
                  isDirtyOutside={isDirtyOutside}
                  noResult={noResult}
                  recommendedModifiers={recommendedModifiers}
                />
              )}
              <Note idx={0} namePrefix="notes" disabled={disabled} />
            </div>

            <Impact
              startingValues={startingValues}
              baseValues={{
                frequency_times: projectedScenario.frequency_times,
                lower: projectedScenario.lower,
                upper: projectedScenario.upper,
              }}
              isHidden={!checkIsModifierStrategy(control.strategy)}
            />
          </div>

          {(control.status === ControllStatus.Abandoned ||
            control.status === ControllStatus.Retired ||
            (!disabled && dirty)) && (
            <ButtonContainer
              style={{ marginLeft: !disabled ? (!values.treat.length && !values.transfer.length ? 0 : 30) : 0 }}
            >
              {(control.status === ControllStatus.Abandoned || control.status === ControllStatus.Retired) && (
                <div className="abandoned-note">
                  <Info />
                  This control won't impact the scenario unless its status changes.
                </div>
              )}
              {!disabled && dirty && (
                <div className="btns">
                  <Button onClick={() => handleSubmit()} primary type="submit" disabled={isSaving || !isValid} loading={isSaving}>
                    Save
                  </Button>
                  <Button onClick={() => formik.resetForm()} secondary disabled={isSaving}>
                    Cancel
                  </Button>
                </div>
              )}
            </ButtonContainer>
          )}

          {err && (
            <>
              <Spacer $px={15} />
              <Poppins px={14} color="error">
                {err}
              </Poppins>
            </>
          )}
        </FormikProvider>
      </Div>
    );
  },
  (prev, next) => _.isEqual(prev, next),
);
